import { trackEvent } from '../analyticsService';
import { ACCOUNT_BILLING_EVENTS } from '../events';
import { getNavigationTrigger } from '../navigationTrigger';

export function useAccountBillingAnalytics() {
  const trackDashboardPageViewed = ({ moduleName, sourcePosition, elementType, lang, isLoggedIn, currentTierId, entitlementKind, userState, testModeEnabled, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.DASHBOARD_PAGE_VIEWED, {
      module_name: moduleName,
      source_position: sourcePosition,
      element_type: elementType,
      lang,
      is_logged_in: isLoggedIn,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      user_state: userState,
      test_mode_enabled: testModeEnabled,
      navigation_trigger: getNavigationTrigger(),
      ...rest
    });
  };

  const trackDashboardSectionViewed = ({ dashboardSection, dashboardContentState, moduleName, sourcePosition, elementName, elementType, currentTierId, entitlementKind, userState, isLoggedIn, testModeEnabled, creditsCycleLimit, creditsCycleUsed, creditsTrialTotal, creditsTrialUsed, usagePercentBucket, pricingPlanCount, billingPeriod, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.DASHBOARD_SECTION_VIEWED, {
      dashboard_section: dashboardSection,
      dashboard_content_state: dashboardContentState,
      module_name: moduleName,
      source_position: sourcePosition,
      element_name: elementName,
      element_type: elementType,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      user_state: userState,
      is_logged_in: isLoggedIn,
      test_mode_enabled: testModeEnabled,
      credits_cycle_limit: creditsCycleLimit,
      credits_cycle_used: creditsCycleUsed,
      credits_trial_total: creditsTrialTotal,
      credits_trial_used: creditsTrialUsed,
      usage_percent_bucket: usagePercentBucket,
      pricing_plan_count: pricingPlanCount,
      billing_period: billingPeriod,
      ...rest
    });
  };

  const trackDashboardStatusSyncCompleted = ({ actionStatus, moduleName, sourcePosition, method, reasonCode, httpStatus, durationMs, currentTierId, entitlementKind, userState, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.DASHBOARD_STATUS_SYNC_COMPLETED, {
      action_status: actionStatus,
      module_name: moduleName,
      source_position: sourcePosition,
      method,
      reason_code: reasonCode,
      http_status: httpStatus,
      duration_ms: durationMs,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      user_state: userState,
      ...rest
    });
  };

  const trackDashboardPricingLoadCompleted = ({ actionStatus, reasonCode, moduleName, sourcePosition, billingPeriod, pricingPlanCount, dashboardContentState, lang, isLoggedIn, currentTierId, entitlementKind, userState, testModeEnabled, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.DASHBOARD_PRICING_LOAD_COMPLETED, {
      action_status: actionStatus,
      reason_code: reasonCode,
      module_name: moduleName,
      source_position: sourcePosition,
      billing_period: billingPeriod,
      pricing_plan_count: pricingPlanCount,
      dashboard_content_state: dashboardContentState,
      lang,
      is_logged_in: isLoggedIn,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      user_state: userState,
      test_mode_enabled: testModeEnabled,
      ...rest
    });
  };

  const trackDashboardOptionChanged = ({ optionName, optionValue, previousValue, billingPeriod, moduleName, sourcePosition, elementName, elementType, method, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.DASHBOARD_OPTION_CHANGED, {
      option_name: optionName,
      option_value: optionValue,
      previous_value: previousValue,
      billing_period: billingPeriod,
      module_name: moduleName,
      source_position: sourcePosition,
      element_name: elementName,
      element_type: elementType,
      method,
      ...rest
    });
  };

  const trackDashboardCtaClicked = ({ ctaType, planId, billingPeriod, billingFlow, contentId, contentType, moduleName, sourcePosition, elementName, elementType, method, lang, isLoggedIn, currentTierId, entitlementKind, userState, isCurrentPlan, isButtonDisabled, testModeEnabled, actionStatus, reasonCode, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.DASHBOARD_CTA_CLICKED, {
      cta_type: ctaType,
      plan_id: planId,
      billing_period: billingPeriod,
      billing_flow: billingFlow,
      content_id: contentId,
      content_type: contentType,
      module_name: moduleName,
      source_position: sourcePosition,
      element_name: elementName,
      element_type: elementType,
      method,
      lang,
      is_logged_in: isLoggedIn,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      user_state: userState,
      is_current_plan: isCurrentPlan,
      is_button_disabled: isButtonDisabled,
      test_mode_enabled: testModeEnabled,
      action_status: actionStatus,
      reason_code: reasonCode,
      ...rest
    });
  };

  const trackBillingSessionStarted = ({ billingFlow, planId, billingPeriod, currentTierId, entitlementKind, requestId, moduleName, sourcePosition, elementName, elementType, method, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.BILLING_SESSION_STARTED, {
      billing_flow: billingFlow,
      plan_id: planId,
      billing_period: billingPeriod,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      request_id: requestId,
      module_name: moduleName,
      source_position: sourcePosition,
      element_name: elementName,
      element_type: elementType,
      method,
      ...rest
    });
  };

  const trackBillingSessionCompleted = ({ billingFlow, actionStatus, reasonCode, errorCode, httpStatus, durationMs, requestId, planId, billingPeriod, currentTierId, entitlementKind, moduleName, sourcePosition, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.BILLING_SESSION_COMPLETED, {
      billing_flow: billingFlow,
      action_status: actionStatus,
      reason_code: reasonCode,
      error_code: errorCode,
      http_status: httpStatus,
      duration_ms: durationMs,
      request_id: requestId,
      plan_id: planId,
      billing_period: billingPeriod,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      module_name: moduleName,
      source_position: sourcePosition,
      ...rest
    });
  };

  const trackBillingExternalRedirectStarted = ({ billingFlow, requestId, planId, billingPeriod, actionStatus, moduleName, sourcePosition, elementName, elementType, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.BILLING_EXTERNAL_REDIRECT_STARTED, {
      billing_flow: billingFlow,
      request_id: requestId,
      plan_id: planId,
      billing_period: billingPeriod,
      action_status: actionStatus,
      module_name: moduleName,
      source_position: sourcePosition,
      element_name: elementName,
      element_type: elementType,
      ...rest
    });
  };

  const trackBillingCheckoutReturnDetected = ({ checkoutMode, billingFlow, returnQueryPresent, moduleName, sourcePosition, lang, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.BILLING_CHECKOUT_RETURN_DETECTED, {
      checkout_mode: checkoutMode,
      billing_flow: billingFlow,
      return_query_present: returnQueryPresent,
      module_name: moduleName,
      source_position: sourcePosition,
      lang,
      ...rest
    });
  };

  const trackBillingCheckoutReturnCompleted = ({ checkoutMode, actionStatus, billingFlow, reasonCode, httpStatus, durationMs, currentTierId, entitlementKind, userState, returnQueryPresent, moduleName, sourcePosition, isLoggedIn, testModeEnabled, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.BILLING_CHECKOUT_RETURN_COMPLETED, {
      checkout_mode: checkoutMode,
      action_status: actionStatus,
      billing_flow: billingFlow,
      reason_code: reasonCode,
      http_status: httpStatus,
      duration_ms: durationMs,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      user_state: userState,
      return_query_present: returnQueryPresent,
      module_name: moduleName,
      source_position: sourcePosition,
      is_logged_in: isLoggedIn,
      test_mode_enabled: testModeEnabled,
      ...rest
    });
  };

  const trackBillingCheckoutConfirmCompleted = ({ actionStatus, billingFlow, checkoutMode, reasonCode, httpStatus, durationMs, currentTierId, entitlementKind, moduleName, sourcePosition, isLoggedIn, testModeEnabled, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.BILLING_CHECKOUT_CONFIRM_COMPLETED, {
      action_status: actionStatus,
      billing_flow: billingFlow,
      checkout_mode: checkoutMode,
      reason_code: reasonCode,
      http_status: httpStatus,
      duration_ms: durationMs,
      current_tier_id: currentTierId,
      entitlement_kind: entitlementKind,
      module_name: moduleName,
      source_position: sourcePosition,
      is_logged_in: isLoggedIn,
      test_mode_enabled: testModeEnabled,
      ...rest
    });
  };

  const trackBillingNoticeViewed = ({ reasonCode, noticeReasonCode, targetRouteName, moduleName, sourcePosition, elementName, elementType, contentType, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.BILLING_NOTICE_VIEWED, {
      reason_code: reasonCode,
      // 0.9.6.7：动作语义与「是哪种弹窗」解耦（归一化见 BillingNoticeModal.resolveNoticeReasonCode）。
      notice_reason_code: noticeReasonCode,
      target_route_name: targetRouteName,
      module_name: moduleName,
      source_position: sourcePosition,
      element_name: elementName,
      element_type: elementType,
      content_type: contentType,
      ...rest
    });
  };

  const trackBillingNoticeActionClicked = ({ ctaType, actionStatus, reasonCode, noticeReasonCode, targetRouteName, moduleName, sourcePosition, elementName, elementType, method, ...rest } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.BILLING_NOTICE_ACTION_CLICKED, {
      cta_type: ctaType,
      action_status: actionStatus,
      reason_code: reasonCode,
      notice_reason_code: noticeReasonCode,
      target_route_name: targetRouteName,
      module_name: moduleName,
      source_position: sourcePosition,
      element_name: elementName,
      element_type: elementType,
      method,
      ...rest
    });
  };

  const trackFeatureGateShown = ({ featureCode, subFeatureCode, gateMode, requiredTier } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.FEATURE_GATE_SHOWN, {
      feature_code: featureCode,
      sub_feature_code: subFeatureCode,
      gate_mode: gateMode,
      required_tier: requiredTier
    });
  };

  const trackFeatureGateCtaClicked = ({ featureCode, subFeatureCode, gateMode, ctaType } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.FEATURE_GATE_CTA_CLICKED, {
      feature_code: featureCode,
      sub_feature_code: subFeatureCode,
      gate_mode: gateMode,
      cta_type: ctaType
    });
  };

  const trackCreditActionFailed = ({ actionCode, reasonCode } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.CREDIT_ACTION_FAILED, {
      action_code: actionCode,
      reason_code: reasonCode
    });
  };

  // 解锁里程碑（quote_ready / cancelled / unlocked）。失败侧仍走 trackCreditActionFailed，
  // 两者共用 action 语汇：失败带 action_code，里程碑带 unlock_stage，可按 request_id 串成漏斗。
  const trackCapabilityUnlockCompleted = ({
    unlockStage,
    featureCode,
    subFeatureCode,
    capabilityCode,
    subjectId,
    creditsRequired,
    requestId
  } = {}) => {
    trackEvent(ACCOUNT_BILLING_EVENTS.CAPABILITY_UNLOCK_COMPLETED, {
      unlock_stage: unlockStage,
      feature_code: featureCode,
      sub_feature_code: subFeatureCode,
      capability_code: capabilityCode,
      subject_id: subjectId,
      // ⚠️ 0 是合法值（如 pro 档免费能力），不能被当成空值丢掉
      credits_required: creditsRequired,
      request_id: requestId
    });
  };

  return {
    trackDashboardPageViewed,
    trackDashboardSectionViewed,
    trackDashboardStatusSyncCompleted,
    trackDashboardPricingLoadCompleted,
    trackDashboardOptionChanged,
    trackDashboardCtaClicked,
    trackBillingSessionStarted,
    trackBillingSessionCompleted,
    trackBillingExternalRedirectStarted,
    trackBillingCheckoutReturnDetected,
    trackBillingCheckoutReturnCompleted,
    trackBillingCheckoutConfirmCompleted,
    trackBillingNoticeViewed,
    trackBillingNoticeActionClicked,
    trackFeatureGateShown,
    trackFeatureGateCtaClicked,
    trackCreditActionFailed,
    trackCapabilityUnlockCompleted
  };
}
